import { CloseButton } from "./LucidePrimitives";
import { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Link,
  Icon,
} from "@chakra-ui/react";
import { CircleAlert, TriangleAlert, Info } from "lucide-react";
import { useStatus } from "../Contexts/StatusContext";

const DISMISS_KEY = "pinpoint_dismissed_status";
const STATUS_PAGE_URL = "https://status.eng.umd.edu";

const SEVERITY_CONFIG = {
  degraded_performance: {
    bg: "yellow.100",
    color: "yellow.800",
    borderColor: "yellow.500",
    icon: CircleAlert,
    dismissable: true,
  },
  partial_outage: {
    bg: "orange.100",
    color: "orange.800",
    borderColor: "orange.500",
    icon: CircleAlert,
    dismissable: true,
  },
  major_outage: {
    bg: "red.100",
    color: "red.800",
    borderColor: "red.500",
    icon: TriangleAlert,
    dismissable: false,
  },
  under_maintenance: {
    bg: "blue.100",
    color: "blue.800",
    borderColor: "blue.500",
    icon: Info,
    dismissable: true,
  },
};

function getLatestUpdateId(incident) {
  if (!incident || !Array.isArray(incident.incident_updates) || incident.incident_updates.length === 0) {
    return null;
  }
  return incident.incident_updates[0].id;
}

function getLatestUpdateBody(incident) {
  if (!incident || !Array.isArray(incident.incident_updates) || incident.incident_updates.length === 0) {
    return "";
  }
  const body = incident.incident_updates[0].body || "";
  return body.length > 120 ? body.slice(0, 120) + "..." : body;
}

function isDismissed(incident) {
  try {
    const dismissed = JSON.parse(localStorage.getItem(DISMISS_KEY));
    if (!dismissed) return false;
    return (
      dismissed.incidentId === incident.id &&
      dismissed.lastUpdateId === getLatestUpdateId(incident)
    );
  } catch {
    return false;
  }
}

function dismissIncident(incident) {
  localStorage.setItem(
    DISMISS_KEY,
    JSON.stringify({
      incidentId: incident.id,
      lastUpdateId: getLatestUpdateId(incident),
    })
  );
}

export default function StatusBanner() {
  const { status, incidents } = useStatus();
  const [dismissed, setDismissed] = useState(false);

  // Pick the most relevant incident (first unresolved one affecting Pinpoint)
  const incident = incidents.length > 0 ? incidents[0] : null;

  // Reset dismissed state when incident or its updates change
  useEffect(() => {
    if (incident) {
      setDismissed(isDismissed(incident));
    } else {
      setDismissed(false);
    }
  }, [incident]);

  // Don't render if operational and no incidents
  if (status === "operational" && !incident) return null;

  // Don't render if dismissed (and dismissable)
  const config = SEVERITY_CONFIG[status] || SEVERITY_CONFIG.degraded_performance;
  if (dismissed && config.dismissable) return null;

  const handleDismiss = () => {
    if (incident) {
      dismissIncident(incident);
    }
    setDismissed(true);
  };

  // Format the display name for the status
  const statusLabel = status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Box
      bg={config.bg}
      color={config.color}
      borderBottom="2px solid"
      borderColor={config.borderColor}
      px={4}
      py={2}
      position="relative"
      zIndex={1000}
    >
      <Flex align="center" justify="center" maxW="1200px" mx="auto" gap={2}>
        <Icon as={config.icon} boxSize={4} flexShrink={0} fill="none" aria-hidden="true" />
        <Text fontSize="sm" textAlign="center">
          <Text as="span" fontWeight="bold">{statusLabel}</Text>
          {incident && (
            <>
              {" — "}
              {incident.name}
              {getLatestUpdateBody(incident) && (
                <>. {getLatestUpdateBody(incident)}</>
              )}
            </>
          )}
          {" "}
          <Link
            href={STATUS_PAGE_URL}
            isExternal
            textDecoration="underline"
            fontWeight="semibold"
          >
            View status &rarr;
          </Link>
        </Text>
        {config.dismissable && (
          <CloseButton
            size="sm"
            onClick={handleDismiss}
            ml={2}
            flexShrink={0}
            opacity={0.6}
            _hover={{ opacity: 1 }}
          />
        )}
      </Flex>
    </Box>
  );
}
